'use strict';

/*
 * OneTrust consent broker.
 *
 * Reads OneTrust config from #global-meta-data, injects the SDK stub
 * (otSDKStub.js) when active, and exposes helpers to read consent group
 * ids, check consent state, and listen for consent changes.
 */

import { hasConsent as hasStoredConsent } from './onetrust-cookie.js';


let initialized = false;

function readOneTrustConfig() {
    const node = document.getElementById('global-meta-data');
    if (!node) {
        return null;
    }
    try {
        const raw = node.textContent.replace(/\\x([0-9a-f]{2})/g,
            (_, pair) => String.fromCharCode(parseInt(pair, 16)));
        const parsed = JSON.parse(raw);
        return parsed && parsed.oneTrust ? parsed.oneTrust : null;
    } catch (e) {
        console.error('[OneTrust] failed to parse #global-meta-data', e);
        return null;
    }
}

const oneTrustConfig = readOneTrustConfig();

/** Returns the Analytics consent group id, or '' if not configured. */
function getAnalyticsConsentGroup() {
    return oneTrustConfig?.analyticsConsentGroup || '';
}

/** Returns the Survey consent group id, or '' if not configured. */
function getSurveyConsentGroup() {
    return oneTrustConfig?.surveyConsentGroup || '';
}

/**
 * True when the OneTrust SDK is enabled and active on this page.
 * When the toggle is off the server never populates oneTrustConfig, so this returns false.
 * @returns {boolean}
 */
function isOneTrustActive() {
    return !!(oneTrustConfig && oneTrustConfig.enabled === true);
}

/**
 * Registers a callback to be fired on every consent change.
 * Returns an unsubscribe function.
 *
 * @param {() => void} callback
 * @returns {() => void} unsubscribe
 */
const consentListeners = [];
function onConsentChanged(callback) {
    if (typeof callback !== 'function') {
        return () => {};
    }
    consentListeners.push(callback);
    return () => {
        const idx = consentListeners.indexOf(callback);
        if (idx !== -1) {
            consentListeners.splice(idx, 1);
        }
    };
}

function fireConsentChanged() {
    for (const fn of consentListeners) {
        try {
            fn();
        } catch (e) {
            console.error('[OneTrust] consent listener threw', e);
        }
    }
}

/**
 * Returns true when the user consented to the given OneTrust group.
 * When the SDK is loaded, its state is authoritative (no cookie fallback).
 * Falls back to the persisted OptanonConsent cookie only when the SDK is absent.
 *
 * @param {string} group OneTrust group id (e.g. 'C0002')
 * @returns {boolean}
 */
function hasConsent(group) {
    if (!group) {
        return false;
    }
    if (window.OneTrust && window.OnetrustActiveGroups) {
        return window.OnetrustActiveGroups.split(',').includes(group);
    }
    return hasStoredConsent(group);
}

function injectSdkStub(src, domainScript, sriKey) {
    const script = document.createElement('script');
    script.type = 'text/javascript';
    script.src = src;
    script.charset = 'UTF-8';
    script.setAttribute('data-domain-script', domainScript);
    if (sriKey) {
        script.integrity = sriKey;
        script.crossOrigin = 'anonymous';
    }
    script.onerror = (e) => {
        console.error('[OneTrust] SDK stub FAILED to load from', src, e);
    };
    document.head.appendChild(script);
}

function init() {
    if (initialized) {
        return;
    }
    initialized = true;

    // oneTrustConfig is null when the toggle is off, so these early returns
    // also guard against executing when the feature is globally disabled.
    if (!oneTrustConfig || oneTrustConfig.enabled !== true) {
        return;
    }
    const key = oneTrustConfig.key;
    const scriptUrl = oneTrustConfig.scriptUrl;
    if (!key || !scriptUrl) {
        return;
    }
    window.addEventListener('OneTrustGroupsUpdated', fireConsentChanged);
    window.addEventListener('OTConsentApplied', fireConsentChanged);
    const previousOptanonWrapper = window.OptanonWrapper;
    window.OptanonWrapper = function () {
        if (typeof previousOptanonWrapper === 'function') {
            try { previousOptanonWrapper(); } catch (e) { /* ignore */ }
        }
        fireConsentChanged();
    };

    const sriKey = oneTrustConfig.sriKey || '';
    injectSdkStub(scriptUrl, key, sriKey);
}

init();

export {
    hasConsent,
    getAnalyticsConsentGroup,
    getSurveyConsentGroup,
    isOneTrustActive,
    onConsentChanged
};
